import { useState, useEffect } from 'react';
import { exportBirLogsToCsv } from '../utils/birLogger';
import { generateDeterministicLicense } from '../utils/mintCore';

export function AdminKeygen() {
  const [adminPassword, setAdminPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);

  const [hardwareFp, setHardwareFp] = useState('');
  const [generatedKey, setGeneratedKey] = useState('');
  const [generatedAt, setGeneratedAt] = useState('');
  const [copied, setCopied] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    // Verify existing session authentication token
    const token = sessionStorage.getItem('cf_admin_auth_token');
    if (token === 'cf-vault-unlocked-2026') {
      setIsAuthenticated(true);
    }
  }, []);

  const handleAuthSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (adminPassword === 'CruzfindAdmin2026!') {
      setIsAuthenticated(true);
      setAuthError(null);
      sessionStorage.setItem('cf_admin_auth_token', 'cf-vault-unlocked-2026');
    } else {
      setAuthError('❌ ACCESS DENIED: INVALID KEY TOKEN');
      setAdminPassword('');
    }
  };

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isGenerating) return;
    setIsGenerating(true);
    try {
      const result = await generateDeterministicLicense({
        hardwareFp: hardwareFp.trim() || 'MASTER',
        priceTier: 1499,
      });
      setGeneratedKey(result.key);
      setGeneratedAt(result.generatedAt);
    } catch (err) {
      console.error('Key generation failed:', err);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopy = () => {
    if (!generatedKey) return;
    navigator.clipboard.writeText(generatedKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSignOut = () => {
    sessionStorage.removeItem('cf_admin_auth_token');
    setIsAuthenticated(false);
    setAdminPassword('');
  };

  const handleExportBackup = () => {
    const storage: Record<string, string> = {};
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        storage[key] = localStorage.getItem(key) || '';
      }
    }

    const backupData = {
      manifestId: 'CF-OFFLINE-BACKUP',
      timestamp: Date.now(),
      registry: storage,
      clientKey: localStorage.getItem('cf_hw_active_signature') || 'N/A'
    };

    const blob = new Blob([JSON.stringify(backupData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cruzfind_offline_backup_${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (!isAuthenticated) {
    return (
      <div className="max-w-md mx-auto my-12 bg-slate-950 border border-slate-900 rounded-3xl p-8 shadow-[0_0_50px_rgba(16,185,129,0.1)] relative overflow-hidden text-white">
        {/* Animated stars / nebula backdrop simulation */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-indigo-950/20 via-slate-950 to-slate-950" />
        <div className="absolute -top-12 -left-12 w-48 h-48 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-12 -right-12 w-48 h-48 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />

        <div className="relative z-10 flex flex-col items-center text-center space-y-6">
          <div className="w-20 h-20 bg-slate-900/60 border border-emerald-500/30 rounded-2xl flex items-center justify-center text-emerald-400 text-3xl shadow-[0_0_20px_rgba(16,185,129,0.15)] animate-pulse">
            🪐
          </div>

          <div className="space-y-2">
            <h2 className="text-md font-extrabold text-slate-100 tracking-wider">
              CRUZFIND CORE - SECURE ADMINISTRATIVE VAULT
            </h2>
            <p className="text-[9px] uppercase font-bold tracking-widest text-emerald-400/80">
              Cryptographic Lock Screen
            </p>
          </div>

          <form onSubmit={handleAuthSubmit} className="w-full space-y-4 text-left">
            <div className="space-y-2">
              <label className="block text-[9px] font-bold text-slate-400 uppercase tracking-widest text-center">
                Master Decryption Key
              </label>
              <input
                type="password"
                value={adminPassword}
                onChange={(e) => setAdminPassword(e.target.value)}
                placeholder="••••••••••••••••"
                className="w-full px-4 py-3 bg-slate-900/80 border border-slate-800 rounded-xl text-xs text-slate-100 placeholder-slate-700 focus:outline-none focus:border-emerald-500 text-center tracking-widest font-mono shadow-inner"
                required
              />
              {authError && (
                <p className="text-[10px] text-rose-500 font-semibold text-center tracking-wide uppercase animate-pulse">
                  {authError}
                </p>
              )}
            </div>

            <button
              type="submit"
              className="w-full py-3.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-slate-950 font-bold rounded-xl text-xs uppercase tracking-wider shadow-[0_0_20px_rgba(16,185,129,0.2)] active:scale-[0.98] transition-all"
            >
              Verify Security Signature
            </button>
            <button
              type="button"
              onClick={() => {
                window.location.hash = '';
                window.location.pathname = '/';
              }}
              className="w-full pt-4 text-[10px] font-mono font-bold tracking-widest text-slate-500 hover:text-slate-300 transition-colors uppercase text-center block"
            >
              ← BUMALIK SA LOGIN SYSTEM TERMINAL
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto my-12 bg-slate-950 border border-slate-900 rounded-3xl p-8 shadow-[0_0_50px_rgba(59,130,246,0.1)] relative overflow-hidden text-white animate-in fade-in zoom-in duration-200">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-blue-950/20 via-slate-950 to-slate-950" />
      <div className="absolute -top-12 -left-12 w-48 h-48 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-12 -right-12 w-48 h-48 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none" />

      <div className="relative z-10 flex flex-col items-center text-center space-y-6">
        <div className="w-16 h-16 bg-blue-500/10 border border-blue-500/20 rounded-2xl flex items-center justify-center text-blue-400 text-3xl shadow-inner animate-pulse">
          🔑
        </div>

        <div className="space-y-1">
          <h2 className="text-xl font-bold text-slate-100 tracking-tight">CruzFind Master Keygen</h2>
          <p className="text-[10px] uppercase font-bold tracking-widest text-blue-450">
            Administrative Key Generator Panel
          </p>
        </div>

        <form onSubmit={handleGenerate} className="w-full space-y-4 text-left">
          <div className="space-y-1.5">
            <label className="block text-[9px] font-bold text-slate-500 uppercase tracking-wider">
              Client Hardware Fingerprint
            </label>
            <input
              type="text"
              value={hardwareFp}
              onChange={(e) => setHardwareFp(e.target.value)}
              placeholder="e.g. CF-HW-DC8DPL"
              className="w-full px-4 py-2.5 bg-slate-900 border border-slate-800 rounded-xl text-xs text-slate-100 placeholder-slate-650 focus:outline-none focus:border-blue-500 font-mono text-center tracking-widest"
            />
            <p className="text-[9.5px] text-slate-500">
              Leave blank to generate the master fallback activation key.
            </p>
          </div>

          <button
            type="submit"
            disabled={isGenerating}
            className="w-full py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-bold rounded-xl text-xs uppercase tracking-wider shadow-lg active:scale-[0.98] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isGenerating ? 'Generating...' : 'Generate Key'}
          </button>
        </form>

        {generatedKey && (
          <div className="w-full space-y-3 animate-in fade-in duration-200">
            <div className="flex items-center gap-2 text-[9px] uppercase tracking-wider text-slate-500 justify-center">
              <div className="h-px bg-slate-800 flex-1" />
              <span>Voucher Output Signature</span>
              <div className="h-px bg-slate-800 flex-1" />
            </div>

            <div className="p-4 bg-slate-900 border border-slate-850 rounded-xl space-y-2">
              <div className="text-sm font-mono text-blue-400 font-bold select-all break-all tracking-wider text-center">
                {generatedKey}
              </div>
              {generatedAt && (
                <div className="text-[9px] text-slate-500 text-center font-mono">
                  Generated: {new Date(generatedAt).toLocaleString()}
                </div>
              )}
              <button
                type="button"
                onClick={handleCopy}
                className="w-full py-2 bg-slate-950 hover:bg-slate-900 border border-slate-800 text-slate-350 hover:text-slate-200 text-[10px] font-bold rounded-lg uppercase transition-all"
              >
                {copied ? '✅ Key Copied to Clipboard' : '📋 Copy Activation Key'}
              </button>
            </div>
          </div>
        )}

        <div className="w-full pt-2 space-y-2">
          <button
            type="button"
            onClick={handleExportBackup}
            className="w-full py-2.5 bg-slate-900 hover:bg-slate-850 border border-slate-800 text-emerald-400 hover:text-emerald-350 font-bold rounded-xl text-[10px] uppercase tracking-wider transition-all"
          >
            📦 Export Offline Backup Package
          </button>
          <button
            type="button"
            onClick={exportBirLogsToCsv}
            className="w-full py-2.5 bg-slate-900 hover:bg-slate-850 border border-slate-800 text-amber-400 hover:text-amber-350 font-bold rounded-xl text-[10px] uppercase tracking-wider transition-all"
          >
            📊 Export BIR Sales Journal CSV
          </button>
        </div>

        <div className="w-full flex items-center justify-between border-t border-slate-900 pt-4">
          <span className="text-[9px] text-slate-500">
            Keys generated satisfy local binding checks.
          </span>
          <button
            type="button"
            onClick={handleSignOut}
            className="text-[9px] font-bold uppercase tracking-wider text-rose-400 hover:text-rose-350 transition-colors"
          >
            Lock Vault
          </button>
        </div>
      </div>
    </div>
  );
}
